import { BaseText, Headline1, Meta, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import usePollSalesOrderStatus from 'hooks/usePollSalesOrderStatus';
import useSubscriptions from 'hooks/useSubscriptions';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking } from 'react-native';
import { ActivateInfoBox } from 'screens/permit/PermitInfoBoxes';
import styled from 'styled-components/native';
import { StyledWrapper } from 'styles/ContainerStyles';
import { BillingFrequency } from 'types/generatedSchemaTypes';
import { getCurrencyString, getPriceForSalesOrder } from 'utils/commonUtils';
import {
  parseFeatureFlagObject,
  SubscriptionLinksDynamicInfo,
} from 'utils/featureFlagUtils';
import SubscriptionLoader from './SubscriptionLoader';

export const StyledLinkText = styled(BaseText)`
  text-decoration-line: underline;
`;

type SubscriptionInfoProps = {
  loading?: boolean;
};

const SubscriptionInfo: React.FC<SubscriptionInfoProps> = ({ loading }) => {
  const { customer } = useContext(AccountContext);
  const {
    flags: { subscription_links },
  } = useContext(FeatureFlagsContext);
  const { t } = useTranslation();

  const { subscription } = useSubscriptions();
  const {
    name,
    countryCode,
    description,
    prices,
    billingInterval,
    status,
    isFirstMonthFree,
  } = subscription ?? {};
  const subscriptionLinks =
    parseFeatureFlagObject<SubscriptionLinksDynamicInfo>(
      subscription_links,
      'subscription_links'
    );
  const infoLink = countryCode
    ? subscriptionLinks[countryCode]?.info
    : undefined;

  const subscriptionProcessing =
    status === 'ACTIVATING' ||
    status === 'WAITING_FOR_ACTIVATION' ||
    status === 'WAITING_FOR_PAYMENT' ||
    status === 'UPDATING';
  const priceString =
    prices && countryCode
      ? getPriceForSalesOrder(
          prices,
          countryCode,
          new Date(),
          (billingInterval ?? 'MONTHLY') as BillingFrequency
        )
      : undefined;
  const descriptionText = description?.[customer.preferredLanguage ?? 'en'];
  const countryText = t(`account.appLocations.${countryCode}`);
  const freePriceText = `0 ${getCurrencyString(countryCode)}`;

  usePollSalesOrderStatus(
    subscription?.uid,
    subscription?.status ?? 'ACTIVE',
    true
  );

  if (loading) {
    return <SubscriptionLoader />;
  }

  return (
    <StyledWrapper>
      <StyledWrapper center mb={10} mt={0}>
        <Icon name="Aimo-Plus" size="xxl" />
      </StyledWrapper>
      {subscriptionProcessing && (
        <StyledWrapper center ml={50} mr={50}>
          <ActivateInfoBox
            salesOrderReceipt={subscription ?? null}
            bgColor={theme.colors.white}
          />
        </StyledWrapper>
      )}
      <Headline1 center>{name}</Headline1>
      <StyledWrapper center mt={10} mb={0}>
        <Meta center color={theme.colors.black}>
          {t('account.aimoSubscription.validCountry', {
            country: countryText,
          })}
        </Meta>
      </StyledWrapper>
      <StyledWrapper center mt={10} mb={0} ml={20} mr={20}>
        <Meta center color={theme.colors.black}>
          {descriptionText}
        </Meta>
        {infoLink && (
          <StyledWrapper mt={5} mb={0}>
            <StyledLinkText
              onPress={() => Linking.openURL(infoLink)}
              center
              color={theme.colors.black}>
              {t('account.aimoSubscription.linkTexts.info')}
            </StyledLinkText>
          </StyledWrapper>
        )}
      </StyledWrapper>
      <StyledWrapper center mt={10} mb={0}>
        {isFirstMonthFree ? (
          <>
            <Headline1 center>{freePriceText}</Headline1>
            <StyledWrapper center mt={10} mb={0}>
              <Meta center color={theme.colors.black}>
                {t('account.aimoSubscription.trialPeriod')}
              </Meta>
            </StyledWrapper>
            <StyledWrapper center mt={4} mb={0}>
              <Meta center color={theme.colors.gray200}>
                {t('account.aimoSubscription.thenPrice', {
                  price: priceString?.totalPrice ?? '',
                })}
              </Meta>
            </StyledWrapper>
          </>
        ) : (
          <Headline1 center>{priceString?.totalPrice}</Headline1>
        )}
      </StyledWrapper>
    </StyledWrapper>
  );
};

export default SubscriptionInfo;
